<template>
  <main class="mx-auto">
    <template v-if="isLoading">
      <div class="flex flex-col gap-6 py-36 px-5 container mx-auto">
        <UtilsSkeletonLoading class="w-full lg:w-3/4 h-36" />
        <UtilsSkeletonLoading class="w-[90%] lg:w-1/2 h-24" />
      </div>
    </template>
    <CommonBanner
      :data="solutionDetail"
      :in-detail="true"
      :light-theme="true"
      text-to="Solutions"
      url="/solutions"
      class="bg-cover bg-no-repeat bg-[#010104]"
      :style="{
        'background-image': `url(${bgImage})`
      }"
      :half-width="true"
    />

    <section
      v-if="
        solutionDetail?.attributes?.headline_challenge ||
        solutionDetail?.attributes?.challenge
      "
      class="container mx-auto py-6 md:py-10"
    >
      <h2 class="section-heading mb-12">
        {{ solutionDetail?.attributes?.headline_challenge || '' }}
      </h2>
      <p
        class="text-sm sm:text-base text-bw-900 w-full md:w-3/4 mb-5 whitespace-pre-line"
      >
        {{ solutionDetail?.attributes?.challenge || '' }}
      </p>
    </section>

    <section class="container mx-auto py-6 md:py-10">
      <h2 class="section-heading text-center md:text-left">
        {{
          solutionDetail.attributes?.headline_service ||
          `Driving Key Business Results for ${solutionDetail.attributes?.title}`
        }}
      </h2>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-y-10 gap-x-14 mt-8">
        <div
          v-for="approach in solutionDetail.attributes?.approaches"
          :key="approach.id"
          class="flex items-start"
        >
          <div class="icon w-10">
            <NuxtImg
              format="webp"
              srcset=""
              loading="lazy"
              class="w-full h-full"
              :src="approach.icon?.data?.attributes?.url || '/'"
              :alt="approach?.title"
              :title="approach?.title"
            />
          </div>
          <div class="ml-4 flex-1">
            <h3 class="font-medium text-lg mb-2">{{ approach?.title }}</h3>
            <div
              class="text-base description"
              v-html="approach?.description"
            ></div>
          </div>
        </div>
      </div>
    </section>

    <section class="container mx-auto pt-6 md:pt-10">
      <div
        class="text-bw-900 content pb-6 md:pb-10"
        v-html="solutionDetail.attributes?.content"
      ></div>
      <WorkCaseStudiesList
        :header-text="
          solutionDetail.attributes?.headline_work ||
          `Digital Projects for ${solutionDetail.attributes?.title} Leaders`
        "
        :filter-solution="solutionDetail.attributes.title"
      />
      <WorkProductList
        :filter-solution="solutionDetail.attributes.title"
        :title="
          solutionDetail?.attributes?.headline_product ||
          `Discover Our Digital Solutions for ${solutionDetail.attributes.title} Teams`
        "
      />

      <CommonFAQ
        :title="
          solutionDetail?.attributes?.headline_faq ||
          `Frequently Asked Questions About ${solutionDetail?.attributes?.title}`
        "
        type="solution"
        :component-id="solutionDetail?.id"
      />

      <WorkArticlesList
        :header-text="
          solutionDetail.attributes?.headline_article ||
          `Expert Insights and Resources for ${solutionDetail.attributes?.title} Leaders`
        "
        :filter-solution="solutionDetail.attributes.title"
      />
    </section>
  </main>
</template>

<script setup>
import { useGlobalStore } from '~/store/global'

const global = useGlobalStore()
const mainSeo = global.mainSeo

const route = useRoute()

const isLoading = ref(true)

const solutionDetail = ref([])

const getSolution = async () => {
  const { data, error, pending } = await useSuitmediaApiData(
    `/api/solutions/${route.params.slug}`,
    {
      query: {
        'fields[0]': 'id',
        'fields[1]': 'title',
        'fields[2]': 'description',
        'fields[3]': 'slug',
        'fields[4]': 'content',
        populate:
          'banner.image,banner.background_image,approaches.icon,seo.image'
      }
    }
  )

  isLoading.value = pending.value

  if (error.value) {
    throw createError({
      statusCode: 404,
      message: `Solution does not exist`
    })
  }

  return data.value
}

const solution = await getSolution()
solutionDetail.value = solution.data

useJsonld({
  '@context': 'https://schema.org/',
  '@type': 'Organization',
  name: solutionDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  url: solutionDetail?.value?.attributes?.url || 'suitmedia.com',
  description:
    solutionDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  image:
    solutionDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    solutionDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})
useSeoMeta({
  title: solutionDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  ogTitle: solutionDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  description:
    solutionDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogDescription:
    solutionDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  ogImage:
    solutionDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    solutionDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png',
  ogType: solutionDetail?.value?.attributes?.seo?.open_graph_type,
  twitterTitle:
    solutionDetail?.value?.attributes?.seo?.seo_title || 'Suitmedia',
  twitterDescription:
    solutionDetail?.value?.attributes?.seo?.seo_description ||
    'Digital Agency Indonesia',
  twitterImage:
    solutionDetail?.value?.attributes?.seo?.image.data?.attributes?.url ||
    solutionDetail.value.attributes?.banner?.background_image?.data?.attributes
      ?.url ||
    mainSeo.attributes?.seo?.image?.data?.attributes?.url ||
    '/icon.png'
})

const bgImage = computed(() => {
  return solutionDetail.value?.attributes?.banner?.background_image?.data
    ?.attributes?.url
})
</script>

<style lang="scss" scoped>
:deep(.content) {
  color: #353535;
  h1,
  h2 {
    font-size: 22px;
    line-height: normal;
    font-weight: 500 !important;
    margin-bottom: 32px;

    @media screen and (min-width: 640px) {
      font-size: 40px;
      line-height: 50px;
    }
  }

  p {
    width: 100%;
    margin-bottom: 1rem;

    @media screen and (min-width: 768px) {
      width: 75%;
    }
  }

  img {
    margin-bottom: 1rem;
    width: 100%;
  }
}
:deep(.description) {
  strong {
    font-weight: 500 !important;
  }
}
</style>
